import React, { useState, useMemo, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { AdminPageLayout, AdminPageHeader } from "../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../common/supplierPageMeta";
import { getPackageAnalytics, getDashboardSummary } from "../../services/dashboardService";
import { useSupplierServices } from "../../hooks/useSupplierServices";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const PERIOD_TABS = [
  { id: "today", label: "Today" },
  { id: "month", label: "Month" },
  { id: "year", label: "Year" },
];

const HERO_ACCENTS = {
  blue: {
    wash: "bg-sky-50/70",
    stripe: "bg-gradient-to-b from-sky-500 to-blue-600",
    icon: "bg-gradient-to-br from-sky-500 to-blue-600 shadow-sky-500/30",
  },
  emerald: {
    wash: "bg-emerald-50/70",
    stripe: "bg-gradient-to-b from-emerald-500 to-teal-600",
    icon: "bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/30",
  },
  amber: {
    wash: "bg-amber-50/70",
    stripe: "bg-gradient-to-b from-amber-400 to-orange-500",
    icon: "bg-gradient-to-br from-amber-400 to-orange-500 shadow-amber-500/30",
  },
  violet: {
    wash: "bg-violet-50/70",
    stripe: "bg-gradient-to-b from-violet-500 to-purple-600",
    icon: "bg-gradient-to-br from-violet-500 to-purple-600 shadow-violet-500/30",
  },
};

/** Pointer-following radial wash; disabled when prefers-reduced-motion is set. */
function FluidSurface({ as: C = "div", className = "", innerClassName = "", children, ...rest }) {
  const ref = useRef(null);
  const reducedRef = useRef(false);
  const { onMouseMove: userMouseMove, onMouseLeave: userMouseLeave, style: userStyle, ...domRest } = rest;

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedRef.current = mq.matches;
    const onChange = () => {
      reducedRef.current = mq.matches;
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const onMove = (e) => {
    if (reducedRef.current) return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const w = r.width || 1;
    const h = r.height || 1;
    const x = ((e.clientX - r.left) / w) * 100;
    const y = ((e.clientY - r.top) / h) * 100;
    el.style.setProperty("--fx", `${x}%`);
    el.style.setProperty("--fy", `${y}%`);
    userMouseMove?.(e);
  };

  const onLeave = (e) => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--fx", "50%");
    el.style.setProperty("--fy", "50%");
    userMouseLeave?.(e);
  };

  return (
    <C
      ref={ref}
      {...domRest}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={["group/fluid relative overflow-hidden", className].filter(Boolean).join(" ")}
      style={{
        "--fx": "50%",
        "--fy": "50%",
        ...(userStyle && typeof userStyle === "object" ? userStyle : {}),
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 opacity-0 transition-opacity duration-300 ease-out motion-reduce:hidden group-hover/fluid:opacity-100"
        style={{
          background:
            "radial-gradient(ellipse 120% 90% at var(--fx, 50%) var(--fy, 50%), rgba(255,255,255,0.55), rgba(255,255,255,0.08) 38%, transparent 58%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 opacity-0 transition-opacity duration-300 ease-out motion-reduce:hidden group-hover/fluid:opacity-100"
        style={{
          background:
            "radial-gradient(ellipse 100% 80% at var(--fx, 50%) var(--fy, 50%), rgba(13,148,136,0.12), transparent 52%)",
        }}
      />
      <div className={["relative z-[1]", innerClassName].filter(Boolean).join(" ")}>{children}</div>
    </C>
  );
}

function HeroStat({ title, value, subtitle, iconClass, accent }) {
  const a = HERO_ACCENTS[accent] || HERO_ACCENTS.blue;
  return (
    <FluidSurface
      className={`rounded-xl border border-white/80 p-3 shadow shadow-slate-200/40 ring-1 ring-slate-100/80 transition-transform duration-300 ease-out will-change-transform hover:scale-[1.02] motion-reduce:transform-none motion-reduce:hover:scale-100 ${a.wash}`}
    >
      <div className={`pointer-events-none absolute inset-y-3 left-0 w-0.5 rounded-full ${a.stripe}`} />
      <div className="pl-2.5">
        <div className="flex items-center justify-between gap-1">
          <p className="text-[11px] font-medium leading-tight text-slate-600">{title}</p>
          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-sm text-white shadow ${a.icon}`}>
            <i className={iconClass} />
          </span>
        </div>
        <p className="mt-1 text-lg font-bold tabular-nums tracking-tight text-slate-900 sm:text-xl">{value}</p>
        {subtitle ? <p className="mt-0.5 text-[10px] leading-snug text-slate-500">{subtitle}</p> : null}
      </div>
    </FluidSurface>
  );
}

function ServiceRow({ name, icon, bookings, revenue, maxBookings, formatCurrency, formatNumber }) {
  const pct = maxBookings > 0 ? Math.min(100, Math.round((bookings / maxBookings) * 100)) : 0;
  return (
    <div className="flex items-center gap-2 border-b border-slate-100 py-2 last:border-0">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
        <i className={`${icon} text-xs`} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate text-xs font-semibold text-slate-900">{name}</p>
          <span className="shrink-0 text-[10px] text-slate-500">
            {formatNumber(bookings)} · <span className="font-medium text-emerald-700">{formatCurrency(revenue)}</span>
          </span>
        </div>
        <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-teal-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

/** One card: your listings & admin review status */
function ListingStatusCard({ title, icon, rows, tone }) {
  const ring = {
    slate: "ring-slate-200/80",
    indigo: "ring-indigo-200/70",
    rose: "ring-rose-200/70",
    cyan: "ring-cyan-200/70",
    violet: "ring-violet-200/70",
    lime: "ring-lime-200/70",
  }[tone] || "ring-slate-200/80";

  return (
    <FluidSurface
      className={`rounded-xl border border-slate-100 bg-white p-3 shadow-sm ring-1 transition-transform duration-300 ease-out hover:scale-[1.01] motion-reduce:transform-none motion-reduce:hover:scale-100 ${ring}`}
    >
      <div className="mb-2 flex items-center gap-1.5 border-b border-slate-100 pb-1.5">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-50 text-slate-600">
          <i className={`${icon} text-xs`} />
        </span>
        <h3 className="text-xs font-bold text-slate-900">{title}</h3>
      </div>
      <dl className="grid grid-cols-2 gap-x-1 gap-y-1 text-[11px] leading-tight">
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="text-slate-500">{k}</dt>
            <dd className="text-right font-semibold text-slate-900">{v}</dd>
          </React.Fragment>
        ))}
      </dl>
    </FluidSurface>
  );
}

const Dashboard = () => {
  const [periodTab, setPeriodTab] = useState("month");
  const navigate = useNavigate();

  const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    return {
      from: sevenDaysAgo.toISOString().split("T")[0],
      to: today.toISOString().split("T")[0],
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDates());
  const { services: servicesProvided } = useSupplierServices();

  const hasPackages =
    servicesProvided.some((s) => {
      const x = String(s).toLowerCase();
      return x.includes("package");
    });

  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
    error: summaryErr,
    refetch: refetchSummary,
    isFetching: summaryFetching,
  } = useQuery({
    queryKey: ["supplier-dashboard-summary"],
    queryFn: getDashboardSummary,
    enabled: servicesProvided.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const { data: analyticsData, isLoading, error } = useQuery({
    queryKey: ["packageAnalytics", dateRange.from, dateRange.to],
    queryFn: () => getPackageAnalytics(dateRange.from, dateRange.to),
    enabled: hasPackages,
  });

  const analytics = analyticsData?.data || {};

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-IN").format(num || 0);
  };

  const chartData = useMemo(() => {
    if (!analytics.daily_bookings || analytics.daily_bookings.length === 0) {
      return [];
    }
    return analytics.daily_bookings.map((day) => ({
      date: new Date(day.date).toLocaleDateString("en-IN", {
        month: "short",
        day: "numeric",
      }),
      bookings: day.bookings || 0,
      revenue: Math.round(day.revenue || 0),
    }));
  }, [analytics.daily_bookings]);

  const periods = summary?.periods || {};
  const active = periods[periodTab] || {};
  const b = active.bookings || {};
  const rev = active.revenue_completed_payments || {};
  const pend = active.pending_payments || {};
  const inv = summary?.inventory_status || {};
  const alerts = summary?.alerts || {};
  const supplierName = summary?.supplier?.name;

  const serviceRows = useMemo(
    () => [
      { key: "pkg", name: "Packages", icon: "fi fi-rr-box", bookings: b.package_bookings, revenue: rev.package },
      { key: "att", name: "Attractions", icon: "fi fi-rr-star", bookings: b.attraction_bookings, revenue: rev.attraction },
      { key: "evt", name: "Events", icon: "fi fi-rr-calendar", bookings: b.event_bookings, revenue: rev.event },
      { key: "act", name: "Activities", icon: "fi fi-rr-activity", bookings: b.activity_bookings, revenue: rev.activity },
      { key: "rent", name: "Rentals", icon: "fi fi-rr-car", bookings: b.rental_bookings, revenue: rev.rental },
    ],
    [b, rev]
  );

  const maxBookings = useMemo(
    () => Math.max(1, ...serviceRows.map((r) => Number(r.bookings) || 0)),
    [serviceRows]
  );

  if (servicesProvided.length === 0) {
    return (
      <AdminPageLayout>
        <AdminPageHeader
          title={SUPPLIER_PAGE_META.dashboard.title}
          subtitle={SUPPLIER_PAGE_META.dashboard.subtitle}
        />
        <div className="rounded-lg border border-amber-100 bg-amber-50/90 p-3 text-center text-xs text-slate-700">
          No services assigned. Contact support if unexpected.
        </div>
      </AdminPageLayout>
    );
  }

  const dashboardSubtitle = supplierName ? (
    <>
      <span className="font-medium text-slate-800">{supplierName}</span>
      {summary?.generated_at ? (
        <span className="text-slate-400">
          {" "}
          · {new Date(summary.generated_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
        </span>
      ) : null}
    </>
  ) : (
    SUPPLIER_PAGE_META.dashboard.subtitle
  );

  return (
    <AdminPageLayout className="bg-slate-50/80 pb-6">
      <AdminPageHeader
        title={SUPPLIER_PAGE_META.dashboard.title}
        subtitle={dashboardSubtitle}
        actions={
          <div className="flex flex-wrap items-center gap-1.5">
            <FluidSurface className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm" innerClassName="inline-flex">
              {PERIOD_TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setPeriodTab(t.id)}
                  className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${
                    periodTab === t.id ? "bg-teal-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </FluidSurface>
            <FluidSurface className="inline-flex rounded-lg border border-slate-200 bg-white shadow-sm">
              <button
                type="button"
                onClick={() => refetchSummary()}
                disabled={summaryFetching}
                className="relative z-[1] inline-flex h-8 items-center gap-1 rounded-lg bg-transparent px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50/80 disabled:opacity-50"
              >
                <i className={`fi fi-rr-refresh text-[11px] ${summaryFetching ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </FluidSurface>
          </div>
        }
      />

      <div className="space-y-3">

        {Number(alerts.unread_package_enquiries) > 0 ? (
          <FluidSurface className="rounded-lg border border-violet-200 bg-violet-50 p-0 shadow-sm">
            <Link
              to="/enquiry"
              className="relative z-[1] flex items-center gap-2 rounded-lg px-3 py-2 text-xs text-violet-900 transition-transform duration-300 hover:scale-[1.01] motion-reduce:hover:scale-100"
            >
              <i className="fi fi-rr-envelope" />
              <span className="font-semibold">{formatNumber(alerts.unread_package_enquiries)} unread enquiries</span>
              <i className="fi fi-rr-angle-small-right ml-auto" />
            </Link>
          </FluidSurface>
        ) : null}

        {summaryError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            {summaryErr?.message || "Could not load summary."}
          </div>
        ) : null}

        <section className="space-y-3">
          {summaryLoading ? (
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-20 animate-pulse rounded-xl bg-slate-200/60" />
              ))}
            </div>
          ) : summary ? (
            <>
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                <HeroStat
                  title="Bookings"
                  value={formatNumber(b.bookings_total)}
                  subtitle="This range"
                  iconClass="fi fi-rr-book-alt"
                  accent="blue"
                />
                <HeroStat
                  title="Collected"
                  value={formatCurrency(rev.total)}
                  subtitle="Paid"
                  iconClass="fi fi-rr-money"
                  accent="emerald"
                />
                <HeroStat
                  title="Pending"
                  value={formatCurrency(pend.amount)}
                  subtitle={`${formatNumber(pend.count)} txn`}
                  iconClass="fi fi-rr-hourglass-end"
                  accent="amber"
                />
                <HeroStat
                  title="Enquiries"
                  value={formatNumber(active.package_enquiries)}
                  subtitle="Packages"
                  iconClass="fi fi-rr-envelope"
                  accent="violet"
                />
              </div>

              <FluidSurface className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-transform duration-300 hover:scale-[1.005] motion-reduce:hover:scale-100">
                <h3 className="text-xs font-bold text-slate-800">By service</h3>
                <div className="mt-1">
                  {serviceRows.map((row) => (
                    <ServiceRow
                      key={row.key}
                      name={row.name}
                      icon={row.icon}
                      bookings={row.bookings}
                      revenue={row.revenue}
                      maxBookings={maxBookings}
                      formatCurrency={formatCurrency}
                      formatNumber={formatNumber}
                    />
                  ))}
                </div>
              </FluidSurface>

              <div>
                <h3 className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">Listings</h3>
                <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-5">
                  <ListingStatusCard
                    title="Packages"
                    icon="fi fi-rr-box"
                    tone="indigo"
                    rows={[
                      ["Total", formatNumber(inv.packages?.total)],
                      ["Drafts", formatNumber(inv.packages?.drafts)],
                      ["Awaiting approval", formatNumber(inv.packages?.approval_pending)],
                      ["Approved", formatNumber(inv.packages?.approval_approved)],
                      ["Rejected", formatNumber(inv.packages?.approval_rejected)],
                      ["Live & active", formatNumber(inv.packages?.active_published)],
                    ]}
                  />
                  <ListingStatusCard
                    title="Attractions"
                    icon="fi fi-rr-star"
                    tone="rose"
                    rows={[
                      ["Total", formatNumber(inv.attractions?.total)],
                      ["Approved", formatNumber(inv.attractions?.approved)],
                      ["Pending review", formatNumber(inv.attractions?.pending)],
                    ]}
                  />
                  <ListingStatusCard
                    title="Events"
                    icon="fi fi-rr-calendar"
                    tone="cyan"
                    rows={[
                      ["Total", formatNumber(inv.events?.total)],
                      ["Drafts", formatNumber(inv.events?.drafts)],
                      ["Awaiting approval", formatNumber(inv.events?.approval_pending)],
                      ["Approved", formatNumber(inv.events?.approval_approved)],
                      ["Live", formatNumber(inv.events?.live)],
                    ]}
                  />
                  <ListingStatusCard
                    title="Activities"
                    icon="fi fi-rr-activity"
                    tone="violet"
                    rows={[
                      ["Total", formatNumber(inv.activities?.total)],
                      ["Awaiting approval", formatNumber(inv.activities?.approval_pending)],
                      ["Approved", formatNumber(inv.activities?.approval_approved)],
                      ["Rejected", formatNumber(inv.activities?.approval_rejected)],
                      ["Live", formatNumber(inv.activities?.live)],
                    ]}
                  />
                  <ListingStatusCard
                    title="Rentals"
                    icon="fi fi-rr-car"
                    tone="lime"
                    rows={[
                      ["Total items", formatNumber(inv.rentals?.total)],
                      ["Awaiting approval", formatNumber(inv.rentals?.approval_pending)],
                      ["Approved", formatNumber(inv.rentals?.approval_approved)],
                      ["Rejected", formatNumber(inv.rentals?.approval_rejected)],
                      ["Active & approved", formatNumber(inv.rentals?.active)],
                    ]}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <FluidSurface className="rounded-lg border border-slate-200 bg-white shadow-sm transition-colors hover:border-teal-300">
                  <button
                    type="button"
                    onClick={() => navigate("/transactions")}
                    className="relative z-[1] w-full rounded-lg bg-transparent p-2.5 text-left text-xs"
                  >
                    <i className="fi fi-rr-money mb-0.5 block text-sm text-teal-600" />
                    <span className="font-semibold text-slate-900">Transactions</span>
                  </button>
                </FluidSurface>
                <FluidSurface className="rounded-lg border border-slate-200 bg-white shadow-sm transition-colors hover:border-teal-300">
                  <button
                    type="button"
                    onClick={() => navigate("/booking")}
                    className="relative z-[1] w-full rounded-lg bg-transparent p-2.5 text-left text-xs"
                  >
                    <i className="fi fi-rr-calendar-check mb-0.5 block text-sm text-teal-600" />
                    <span className="font-semibold text-slate-900">Bookings</span>
                  </button>
                </FluidSurface>
                <FluidSurface className="rounded-lg border border-slate-200 bg-white shadow-sm transition-colors hover:border-teal-300">
                  <button
                    type="button"
                    onClick={() => navigate("/enquiry")}
                    className="relative z-[1] w-full rounded-lg bg-transparent p-2.5 text-left text-xs"
                  >
                    <i className="fi fi-rr-envelope mb-0.5 block text-sm text-teal-600" />
                    <span className="font-semibold text-slate-900">Enquiries</span>
                  </button>
                </FluidSurface>
              </div>
            </>
          ) : null}
        </section>

        {/* —— Package deep-dive (charts) —— */}
        {hasPackages && (
          <>
            <div className="border-t border-slate-200 pt-4">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-sm font-bold text-slate-900">Packages · charts</h2>
                <FluidSurface
                  className="flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs shadow-sm"
                  innerClassName="flex flex-wrap items-center gap-1.5"
                >
                  <span className="text-slate-500">From</span>
                  <input
                    type="date"
                    value={dateRange.from}
                    onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                    className="border-0 bg-transparent p-0 text-slate-800 focus:ring-0"
                  />
                  <span className="text-slate-300">|</span>
                  <span className="text-slate-500">To</span>
                  <input
                    type="date"
                    value={dateRange.to}
                    onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                    max={new Date().toISOString().split("T")[0]}
                    className="border-0 bg-transparent p-0 text-slate-800 focus:ring-0"
                  />
                  <button
                    type="button"
                    onClick={() => setDateRange(getDefaultDates())}
                    className="rounded bg-teal-50 px-2 py-0.5 font-semibold text-teal-800 hover:bg-teal-100"
                  >
                    Reset
                  </button>
                </FluidSurface>
              </div>

              <div className="space-y-3">
                  {isLoading ? (
                    <div className="flex justify-center py-10">
                      <div className="h-7 w-7 animate-spin rounded-full border-2 border-teal-600 border-t-transparent" />
                    </div>
                  ) : error ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">{error.message}</div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <FluidSurface className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
                          <p className="text-[10px] font-semibold uppercase text-slate-500">Bookings (paid)</p>
                          <p className="text-xl font-bold text-slate-900">{formatNumber(analytics.bookings?.total_bookings)}</p>
                        </FluidSurface>
                        <FluidSurface className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
                          <p className="text-[10px] font-semibold uppercase text-slate-500">Revenue</p>
                          <p className="text-xl font-bold text-slate-900">{formatCurrency(analytics.bookings?.total_revenue)}</p>
                        </FluidSurface>
                      </div>

                      {chartData.length > 0 && (
                        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                          <FluidSurface className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
                            <h3 className="mb-1 text-xs font-bold text-slate-800">Bookings</h3>
                            <ResponsiveContainer width="100%" height={200}>
                              <LineChart data={chartData} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
                                <YAxis stroke="#94a3b8" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} width={28} allowDecimals={false} />
                                <Tooltip contentStyle={{ borderRadius: "8px", fontSize: "11px" }} />
                                <Line type="monotone" dataKey="bookings" stroke="#0d9488" strokeWidth={2} dot={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          </FluidSurface>
                          <FluidSurface className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
                            <h3 className="mb-1 text-xs font-bold text-slate-800">Revenue</h3>
                            <ResponsiveContainer width="100%" height={200}>
                              <LineChart data={chartData} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
                                <YAxis
                                  stroke="#94a3b8"
                                  tick={{ fontSize: 9 }}
                                  tickLine={false}
                                  axisLine={false}
                                  width={32}
                                  tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`}
                                />
                                <Tooltip
                                  contentStyle={{ borderRadius: "8px", fontSize: "11px" }}
                                  formatter={(value, name) =>
                                    name === "revenue" ? [formatCurrency(value), "Revenue"] : [value, "Bookings"]
                                  }
                                />
                                <Line type="monotone" dataKey="revenue" stroke="#059669" strokeWidth={2} dot={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          </FluidSurface>
                        </div>
                      )}

                      <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
                        {[
                          { label: "Groups", value: analytics.overview?.total_groups, icon: "folder", color: "bg-blue-50 text-blue-600" },
                          { label: "Pkgs", value: analytics.overview?.total_packages, icon: "box", color: "bg-indigo-50 text-indigo-600" },
                          { label: "Active", value: analytics.overview?.active_packages, icon: "check-circle", color: "bg-teal-50 text-teal-700" },
                          { label: "OK", value: analytics.overview?.approved_packages, icon: "badge-check", color: "bg-emerald-50 text-emerald-600" },
                          { label: "Wait", value: analytics.overview?.pending_packages, icon: "time-fast", color: "bg-amber-50 text-amber-600" },
                          { label: "No", value: analytics.overview?.rejected_packages, icon: "cross-circle", color: "bg-red-50 text-red-600" },
                        ].map((item, index) => (
                          <FluidSurface key={index} className="rounded-lg border border-slate-100 bg-white p-2 text-center shadow-sm">
                            <div className={`mx-auto mb-0.5 flex h-6 w-6 items-center justify-center rounded-md ${item.color}`}>
                              <i className={`fi fi-rr-${item.icon} text-[10px]`} />
                            </div>
                            <p className="text-[9px] font-bold uppercase text-slate-500">{item.label}</p>
                            <p className="text-sm font-bold text-slate-900">{formatNumber(item.value)}</p>
                          </FluidSurface>
                        ))}
                      </div>

                      <div className="grid grid-cols-4 gap-1.5">
                        {[
                          { label: "Packages", icon: "box", path: "/packages" },
                          { label: "Bookings", icon: "calendar-check", path: "/booking" },
                          { label: "Slots", icon: "clock", path: "/slots" },
                          { label: "Stop", icon: "ban", path: "/stop-sales" },
                        ].map((action, i) => (
                          <FluidSurface
                            key={i}
                            className="rounded-lg border border-slate-200 bg-white shadow-sm transition-colors hover:border-teal-400"
                          >
                            <button
                              type="button"
                              onClick={() => navigate(action.path)}
                              className="relative z-[1] w-full rounded-lg bg-transparent py-2 text-center text-[10px] font-bold text-slate-700"
                            >
                              <i className={`fi fi-rr-${action.icon} mb-0.5 block text-slate-400`} />
                              {action.label}
                            </button>
                          </FluidSurface>
                        ))}
                      </div>
                    </>
                  )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminPageLayout>
  );
};

export default Dashboard;
